import { type ReactNode, type FC, type SyntheticEvent, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Icon from './Icon.tsx'
import { useTranslation } from 'react-i18next'

const noop = (evt: SyntheticEvent) => {
  evt.stopPropagation()
}

const Header: FC<{
  children?: ReactNode
  onMenuClick?: () => void
  onClose?: () => void
}> = ({ children, onMenuClick, onClose }) => (
  <header className="flex h-[60px] flex-row items-center justify-between border-b border-zinc-700 px-[20px]">
    <>
      <span className="text-[16px] font-semibold">{children}</span>
      <div className="flex flex-row items-center justify-center gap-[8px]">
        {onMenuClick && (
          <button
            type="button"
            className="flex flex-row items-center justify-center rounded-[4px] p-[8px] hover:bg-zinc-700"
            onClick={onMenuClick}
          >
            <Icon name="menuVertical" />
          </button>
        )}
        <button
          type="button"
          className="flex flex-row items-center justify-center rounded-[4px] p-[8px] hover:bg-zinc-700"
          onClick={onClose}
        >
          <Icon name="xMark" />
        </button>
      </div>
    </>
  </header>
)

const Content: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex size-full flex-1 flex-row">{children}</div>
)

const Footer: FC<{
  children?: ReactNode
  confirmButton?: ReactNode
  onCancelClick?: () => void
}> = ({ children, confirmButton, onCancelClick }) => {
  const { t } = useTranslation()
  return (
    <footer className="flex w-full flex-row items-center justify-between gap-4 border-t border-zinc-700 px-[20px] py-4">
      {children}
      <div className="flex flex-1 flex-row items-center justify-center gap-4">
        {onCancelClick && (
          <button
            type="button"
            className="inline-flex min-w-[118px] items-center justify-center gap-2 rounded-[4px] bg-zinc-700 px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-600 disabled:opacity-50"
            onClick={onCancelClick}
          >
            {t('ls_generic_cancel')}
          </button>
        )}
        {confirmButton}
      </div>
    </footer>
  )
}

const Dialog: FC<{
  children: ReactNode
  variant?: 'small' | 'large'
  open?: boolean
  onClose?: () => void
  className?: string
}> = ({ children, open, variant = 'large', onClose = () => {}, className }) => {
  const classes = [
    'animate-reveal flex w-full flex-col rounded-[8px] bg-zinc-800 shadow-[0px_0px_32px_0px_#00000059]',
    variant === 'large'
      ? 'max-h-[90vh] min-h-[688px] min-w-[500px] max-w-[900px]'
      : '',
    variant === 'small'
      ? 'max-h-[20dvh] min-h-[240px] min-w-[400px] max-w-[20dvw]'
      : '',
    className ?? '',
  ]

  useEffect(() => {
    if (!onClose) return
    const handleKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  const handleMouseDown = (evt: React.MouseEvent) => {
    if (evt.target === evt.currentTarget) {
      onClose()
    }
  }

  if (!open) return <></>

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onMouseDown={handleMouseDown}
    >
      <div className={classes.join(' ')} onKeyDown={noop} onClick={noop}>
        {children}
      </div>
    </div>,
    document.body
  )
}

export default Object.assign(Dialog, { Header, Content, Footer })
